/**
 * 外殼 —— 標本選單、mode 切換、操作程序節拍器、CPU throttle 宣告、run 歷史。
 *
 * 外殼本身不是量測對象，所以用 React 沒問題。**但有一條硬架構約束**（spec §3.3 / §4.8 / 陷阱 #12）：
 *
 *   iframe 不保護 INP 的 presentation 段。paint 是 renderer 層級的，
 *   外殼面板的 style/layout 成本會落在互動的同一幀，直接被算進標本的 presentation。
 *   標本 #2 的兇手正好就是那一段，所以這裡沒有安全邊際。
 *
 * 對應作法：**訊息處理器一行 setState 都不准寫**，只准寫進 ref；
 * 狀態提交一律走 250ms 的節流閘門（scheduleCommit）。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MEASURE_CONFIG, PROTOCOL_VERSION, buildSpecimenUrl } from '../protocol';
import type {
  CpuThrottle,
  HostMessage,
  InteractionSample,
  LoafSample,
  RunConditions,
  RunResult,
  SpecimenId,
  SpecimenMessage,
  SpecimenMeta,
  SpecimenMetrics,
  SpecimenModeDef,
  SpecimenReady,
} from '../protocol';
import { SPECIMENS, getSpecimen } from '../specimens';
// ⚠️ RunStats 與 refreshHz 各自只准有一份實作。
// 同一個專案裡兩份 median 定義遲早會對不起來，而那種不一致沒有任何徵兆：
// tsc 不會抱怨（兩邊都吐出合法的 RunStats），面板不會抱怨（數字都在合理範圍），
// 你只會在某天發現同一組樣本算出兩個 median。RunStats.median 是可重現性的判定依據，
// 所以「哪一條公式跑了」是會影響結論的事。
import { computeRunStats } from '../measure/metrics';
import { buildDeviceProfile, measureRefreshHz } from '../measure/device';
import { Panel } from './Panel';
import { startLoafObserver } from './loaf';
// 外殼自己的樣式。走 JS import 而不是在 measure.html 手寫 <link>，理由與 panel.css 相同：
// 這支檔案在 entry chunk 的 import graph 裡，build 會把它跟 panel.css 抽成同一份 CSS 資產
// 並自動插進 dist 的 <head>。手寫 <link> 只會多一份要自己維護的路徑。
// ⚠️ 樣式紀律（禁 transition / fixed / 盒模型 hover…）寫在 shell.css 的檔頭，動它之前先讀。
import './shell.css';

/**
 * LoAF 是否可用。判斷式跟 loaf.ts 裡的一樣 —— 那邊決定要不要註冊 observer，
 * 這邊決定面板要不要顯示警告。宣告 Chromium-only，所以到此為止，不寫 fallback（spec §5.3）。
 */
const LOAF_SUPPORTED = PerformanceObserver.supportedEntryTypes.includes('long-animation-frame');

/** LoAF 一輪可能上百幀；十次互動的 protocol 用不到那麼多，留最近 60 幀就夠算最嚴重的那一幀 */
const LOAF_BUFFER_MAX = 60;
const NOTE_MAX = 20;

const THROTTLE_OPTIONS: CpuThrottle[] = ['1x', '4x', '6x', 'unknown'];

/** requires 的人話版本。UI 必須標，否則使用者會以為某個 mode 壞掉，其實是瀏覽器沒有那個 API */
const REQUIRES_NOTE: Record<NonNullable<SpecimenModeDef['requires']>[number], string> = {
  'scheduler.yield': '需要 scheduler.yield（Safari 沒有）',
  'web-worker': '需要 Web Worker',
  'content-visibility': '需要 content-visibility',
};

/** modes[0] 依協定必須是病變版本 —— 先讓人痛，再給解藥（spec §2） */
function firstMode(meta: SpecimenMeta): string {
  return [...meta.modes].sort((a, b) => a.order - b.order)[0].id;
}

let runCounter = 0;
function nextRunId(): string {
  runCounter += 1;
  return `run-${runCounter}`;
}

function nowEpoch(): number {
  return performance.timeOrigin + performance.now();
}

/**
 * 深連結 `?specimen=<id>&mode=<modeId>` —— **B 類量測的正確性前提，不是便利功能。**
 *
 * B 類切 mode 會重載 iframe 的 document，但 iframe 與外殼共用同一條 renderer 主執行緒：
 * 前一份 document 的拆除與殘留工作落在**新 document 的時鐘之內**，而 LCP 取的是
 * `entry.startTime`（以新 document 的 timeOrigin 起算）。於是 LCP 帶著一個由
 * 「前導 mode 有多重」決定的加項。
 *
 * 2026-07-26 實測，單一變因、零重疊：
 *   標本 #2 的 fixed-virtual（141 個節點）
 *     前導 broken                   LCP  1896 / 2616 / 2988 ms
 *     前導 fixed-content-visibility LCP  9812 / 10620 / 16504 ms
 *   差距全部落在新 document 的 responseEnd → DCL 之間（responseEnd 一律 13~262ms，
 *   不是抓取問題），切換前插 15 秒靜置排不掉。同一份量測裡 141 個節點的 document
 *   載入耗時是 40,021 個節點那份的五倍 —— 那個數字結構上不可能在描述標本。
 *
 * 舊的量測順序（每輪都 `for m of modes`）讓每個 mode 的前導永遠相同，**於是污染是常數**：
 * 三輪離散度看起來很漂亮，而它靜靜地烙進臂間比值裡。輪轉順序只是把它掀開。
 *
 * 有了這個參數，每一筆 B 類樣本都從一次全新的 measure.html 導覽開始，
 * 目標 mode 就是首載的那一份 document，前導成本歸零。
 */
/**
 * 沒帶參數時要開哪一個標本。
 *
 * **不是 `SPECIMENS[0]`** —— 那是 `00-calibration`，而校準件不是六個標本之一，
 * 它是驗收工具（每個負載都有解析解可以反推，用來證明量測層本身沒說謊）。
 * 首頁花了整頁講六個標本，點「進量測台 →」卻落在第七個沒有病理故事的頁面上。
 *
 * 靠 id 找、不動 `SPECIMENS` 的順序：校準件排第一是它在按鈕列的位置，
 * 那個順序是量測工具與截圖都依賴的東西，不該為了預設值去動它。
 * 找不到就退回 `SPECIMENS[0]`，因為「沒有標本可開」比「開錯標本」更糟。
 */
const DEFAULT_SPECIMEN_ID = '01-main-thread-block';

function initialFromUrl(): { meta: SpecimenMeta; mode: string; cpu: CpuThrottle } {
  const fallbackMeta = getSpecimen(DEFAULT_SPECIMEN_ID as SpecimenId) ?? SPECIMENS[0];
  const fallback = { meta: fallbackMeta, mode: firstMode(fallbackMeta), cpu: 'unknown' as CpuThrottle };
  if (typeof location === 'undefined') return fallback;

  const q = new URLSearchParams(location.search);

  /**
   * `cpu` 是**宣告**，跟下拉選單完全同一個語意（protocol.ts:55「無法從 JS 偵測」）。
   * 它必須能從 URL 帶進來：deep-link 每量一筆就重載整個外殼，選單狀態隨之清空，
   * 而「載入後再補按一次選單」那個 `evaluate()` 會插進正在量的載入期。
   */
  const wantCpu = q.get('cpu');
  const cpu: CpuThrottle = wantCpu === '1x' || wantCpu === '4x' || wantCpu === '6x' ? wantCpu : 'unknown';

  const wantSpecimen = q.get('specimen');
  if (wantSpecimen === null) return { ...fallback, cpu };

  // 認不得的 id 一律退回預設，不擲錯：量測工具打錯字時該看到「量到的是別的標本」，
  // 而 snapshot.mode / snapshot.specimenId 會把它報出來
  const meta = getSpecimen(wantSpecimen as SpecimenId);
  if (meta === undefined) return { ...fallback, cpu };

  const wantMode = q.get('mode');
  const modeOk = wantMode !== null && meta.modes.some((m) => m.id === wantMode);
  return { meta, mode: modeOk ? (wantMode as string) : firstMode(meta), cpu };
}

const INITIAL = initialFromUrl();
const INITIAL_META = INITIAL.meta;
const INITIAL_MODE = INITIAL.mode;
const INITIAL_CPU = INITIAL.cpu;

/** 面板真正吃到的東西。所有欄位都只透過 250ms 的節流閘門更新 */
interface PanelBuffer {
  ready: SpecimenReady | null;
  metrics: SpecimenMetrics | null;
  loaf: LoafSample[];
  notes: string[];
}

function emptyBuffer(notes: string[] = []): PanelBuffer {
  return { ready: null, metrics: null, loaf: [], notes };
}

/**
 * 節拍器 —— 十次連打與十次每秒一下是不同的實驗（spec §2 / §5.1 第 4 項）。
 *
 * 刻意獨立成一個元件、自己持有 tick 狀態：這樣每次翻拍只重繪這一小塊，
 * 不會把整個面板拖進同一幀重繪。翻拍的時間點正好是操作者要點下去的時間點，
 * 這裡多一次 App 級的 re-render 就是直接往 presentation 段裡加料。
 */
function Metronome({ intervalMs, repetitions }: { intervalMs: number; repetitions: number }) {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let n = 0;
    const id = window.setInterval(() => {
      n += 1;
      setTick(n);
      if (n >= repetitions) window.clearInterval(id);
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs, repetitions]);

  if (tick >= repetitions) {
    return <span className="shell-metro">節拍結束（{repetitions} 拍）—— 按「重跑」開下一輪</span>;
  }
  return (
    <span className="shell-metro">
      節拍 <b className="shell-metro__mark">{tick % 2 === 0 ? '○' : '●'}</b> 第 {tick} /{' '}
      {repetitions} 拍（每次記號翻面時點一下）
    </span>
  );
}

export function App() {
  const [sessionId] = useState(() => `sid-${Date.now().toString(36)}`);
  const [specimenId, setSpecimenId] = useState<SpecimenId>(INITIAL_META.id);
  const [mode, setMode] = useState<string>(INITIAL_MODE);
  const [runId, setRunId] = useState<string>(() => nextRunId());
  const [cpuThrottle, setCpuThrottle] = useState<CpuThrottle>(INITIAL_CPU);
  const [refreshHz, setRefreshHz] = useState(0);
  const [history, setHistory] = useState<RunResult[]>([]);
  /**
   * web-vitals 交叉驗證。**預設關閉**：web-vitals 自己會註冊一整組 PerformanceObserver，
   * 常態開著等於讓「用來驗 baseline 的工具」變成污染 baseline 的來源（陷阱 #7）。
   *
   * 這個開關是 Phase 2 補的。在此之前 `?validate=1` 的機制早就實作好了
   *（`runtime.ts` 的 validate 分支、`buildSpecimenUrl` 的 validate 參數），
   * 但**外殼從來沒有任何地方會把它加進 URL** —— 也就是說那整段程式碼在 UI 上不可達，
   * 驗收第 8 條只能靠手改網址。功能寫了卻按不到，跟沒寫的差別只在它會通過 typecheck。
   */
  const [validate, setValidate] = useState(false);
  const [view, setView] = useState<PanelBuffer>(() => emptyBuffer());
  /**
   * iframe 的 src 是**狀態，不是衍生值**。
   * 寫成 buildSpecimenUrl(meta, { mode, ... }) 直接放進 JSX 的話，A 類即時切換會改到 URL，
   * iframe 就被 React 重載了 —— 那正好是 A 類最不該發生的事（spec §3.4）。
   */
  const [iframeSrc, setIframeSrc] = useState(() =>
    buildSpecimenUrl(INITIAL_META, {
      mode: INITIAL_MODE,
      t: String(Date.now()),
      sid: sessionId,
    }),
  );

  const meta = getSpecimen(specimenId) ?? INITIAL_META;
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  /**
   * 每次要換 iframe 網址都走這裡。三個呼叫點（換標本、B 類換 mode、切 validate）
   * 各自組一次 URL 的話，遲早有一個忘了帶 validate 或忘了帶 cache buster，
   * 而症狀是「這個組合下交叉驗證莫名其妙沒開」。
   */
  function specimenUrl(target: SpecimenMeta, modeId: string, wantValidate: boolean): string {
    return buildSpecimenUrl(target, {
      mode: modeId,
      t: String(Date.now()),
      sid: sessionId,
      // 一定要傳 undefined 而不是 '0' 或 ''：iframe 端是 `=== '1'` 判斷，
      // 但 URLSearchParams 對每個值做 ToString，帶了就會多出一個沒意義的參數。
      validate: wantValidate ? '1' : undefined,
    });
  }

  // ── 訊息處理器只讀 ref，不讀 state：它註冊一次就不再重建，讀 state 會讀到舊值 ──
  const specimenIdRef = useRef<SpecimenId>(INITIAL_META.id);
  const modeRef = useRef<string>(INITIAL_MODE);
  const runIdRef = useRef<string>(runId);
  const runStartedAtRef = useRef<number>(nowEpoch());
  /** 本輪累計的有效互動，依 interactionId 去重取 max —— 分組是全清單最重要的一項（陷阱 #1） */
  const runSamplesRef = useRef<Map<number, InteractionSample>>(new Map());
  const lastSeqRef = useRef<number | null>(null);
  /** 已送出 host:reset、還沒收到標本端 seq 歸零後的第一批。這期間的 metrics 全是上一輪的 */
  const awaitingResetRef = useRef(false);
  const bufRef = useRef<PanelBuffer>(emptyBuffer());
  const dirtyRef = useRef(false);
  const timerRef = useRef<number | null>(null);

  // ── 250ms 節流閘門（架構約束，不是最佳化）──────────────────────────
  const commit = useCallback(() => {
    dirtyRef.current = false;
    const b = bufRef.current;
    setView({ ready: b.ready, metrics: b.metrics, loaf: [...b.loaf], notes: [...b.notes] });
  }, []);

  const scheduleCommit = useCallback(() => {
    dirtyRef.current = true;
    if (timerRef.current !== null) return;
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      if (dirtyRef.current) commit();
    }, MEASURE_CONFIG.flushIntervalMs);
  }, [commit]);

  const note = useCallback((text: string) => {
    const notes = bufRef.current.notes;
    notes.push(`[${runIdRef.current}] ${text}`);
    if (notes.length > NOTE_MAX) notes.splice(0, notes.length - NOTE_MAX);
  }, []);

  const post = useCallback(
    (msg: HostMessage) => {
      // targetOrigin 寫死同源。iframe 端也只收同源訊息，兩邊都關（驗收第 13 條的精神）
      iframeRef.current?.contentWindow?.postMessage(msg, window.location.origin);
    },
    [],
  );

  // ── refreshHz 實測。120Hz 螢幕上「16.7ms 算掉幀」是錯的（spec §5.1 第 12 項）──
  // 實作在 measure/device.ts，這裡不重寫一份：那邊會把結果**吸附**到常見面板更新率。
  // 不吸附的話同一塊 60Hz 螢幕會依當下雜訊寫進 58 / 59 / 60，而 refreshHz 是
  // RunConditions 的一部分 —— 三筆條件不同的 run 在本站的規則下就不能互相比較，
  // 明明它們是同一塊螢幕。
  useEffect(() => {
    void measureRefreshHz().then(setRefreshHz);
  }, []);

  // ── LoAF 觀測（外殼側，不走 postMessage）──────────────────────────
  useEffect(() => {
    const stop = startLoafObserver({
      // 每次重載 / 換標本 contentWindow 都是新物件，所以用 callback 而不是抓一次
      getSpecimenWindow: () => iframeRef.current?.contentWindow ?? null,
      onSample: (s) => {
        // observer callback 是非同步派送的：主執行緒忙的時候，前一輪的 entry 會在
        // 重跑之後才送到。不擋掉的話，新一輪的「最嚴重的一幀」會是上一輪那個 300ms
        // 忙迴圈 —— 跟 metrics 批次擋舊 mode 是同一個理由，兩輪的樣本不准混。
        if (s.start < runStartedAtRef.current) return;
        const buf = bufRef.current.loaf;
        buf.push(s);
        if (buf.length > LOAF_BUFFER_MAX) buf.splice(0, buf.length - LOAF_BUFFER_MAX);
        scheduleCommit();
      },
    });
    return stop;
  }, [scheduleCommit]);

  // ── postMessage 收信 ───────────────────────────────────────────
  useEffect(() => {
    function onMessage(ev: MessageEvent): void {
      // 三層驗證：同源、協定版本、sessionId。任何一層不過就整包丟掉，不留痕跡。
      if (ev.origin !== window.location.origin) return;
      const msg = ev.data as SpecimenMessage | null;
      if (!msg || typeof msg !== 'object' || msg.v !== PROTOCOL_VERSION) return;
      if (msg.sessionId !== sessionId) return;

      switch (msg.type) {
        case 'specimen:ready': {
          if (msg.specimenId !== specimenIdRef.current) {
            note(`⚠ ready 的 specimenId 是 ${msg.specimenId}，外殼以為是 ${specimenIdRef.current}`);
          }
          bufRef.current.ready = msg;
          post({
            type: 'host:init',
            v: PROTOCOL_VERSION,
            sessionId,
            specimenId: msg.specimenId,
            mode: modeRef.current,
            measure: MEASURE_CONFIG,
          });
          scheduleCommit();
          break;
        }

        case 'specimen:mode-changed': {
          note(`mode → ${msg.mode}，warmup 到 ${Math.round(msg.warmupUntil - msg.at)}ms 後`);
          scheduleCommit();
          break;
        }

        case 'specimen:metrics': {
          // 切 mode 的瞬間可能還有一批舊 mode 的 metrics 在路上。混進來 = 兩個 mode 的樣本
          // 掛在同一輪底下，而 n<50 時 INP 就是 max，一筆就足以變成你報告的那個數字。
          if (msg.mode !== modeRef.current) break;

          // 重跑後的閘門：標本端 reset 會把 seq 歸零，所以新一輪的第一批一定是 seq === 1。
          // 在那之前收到的都還是上一輪的樣本（mode 沒變，上面那道 mode 檢查擋不掉）。
          if (awaitingResetRef.current) {
            if (msg.seq !== 1) break;
            awaitingResetRef.current = false;
          }

          if (lastSeqRef.current !== null && msg.seq !== lastSeqRef.current + 1) {
            note(`⚠ seq 跳號：預期 ${lastSeqRef.current + 1}，收到 ${msg.seq}（有一批 metrics 掉了）`);
          }
          lastSeqRef.current = msg.seq;

          for (const s of msg.interactions) {
            if (s.warmup) continue; // 暖機樣本一律不入帳（陷阱 #1 的第二個坑）
            const prev = runSamplesRef.current.get(s.interactionId);
            if (!prev || s.duration > prev.duration) runSamplesRef.current.set(s.interactionId, s);
          }

          bufRef.current.metrics = msg;
          scheduleCommit();
          break;
        }

        case 'specimen:error': {
          note(`‼ 標本錯誤（${msg.stage}）：${msg.message}`);
          scheduleCommit();
          break;
        }
      }
    }

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [sessionId, note, post, scheduleCommit]);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
  }, []);

  // ── 凍結條件快照。可重現的宣稱只在同一組 conditions 之間成立 ──────────
  const conditions: RunConditions = useMemo(() => {
    return {
      device: buildDeviceProfile(cpuThrottle, refreshHz),
      viewport: meta.viewport,
      protocol: meta.protocol,
      buildId: __BUILD_ID__, // 換一版 build 就不是同一組條件，數字不可跨版比較
      protocolVersion: PROTOCOL_VERSION,
      measure: MEASURE_CONFIG,
    };
  }, [cpuThrottle, refreshHz, meta]);

  // ── run 生命週期 ──────────────────────────────────────────────
  /** 把剛跑完的那一輪存進歷史。**永遠不丟棄前一輪** —— 那是可重現性的唯一證據 */
  function finalizeRun(): void {
    const samples = [...runSamplesRef.current.values()].sort((a, b) => a.startTime - b.startTime);
    const last = bufRef.current.metrics;

    /*
     * 「這一輪值不值得入帳」的判準取自標本的主指標，不是「有沒有互動樣本」。
     *
     * 舊寫法是 `samples.length === 0 → return`，那對 INP 系的標本是對的
     * （換標本／切 mode 都會經過這裡，不擋的話歷史會塞滿空白輪）。
     * 但標本 #4／#6 的主指標是 droppedFrames，它們一輪下來本來就零筆互動 ——
     * 舊寫法會把它們每一輪都丟掉，而面板上只會顯示「還沒有完成的 run」。
     */
    const inpBased = meta.primaryMetric.startsWith('inp');
    if (inpBased ? samples.length === 0 : last === null) return;

    const finished: RunResult = {
      runId: runIdRef.current,
      specimenId: specimenIdRef.current,
      mode: modeRef.current,
      startedAt: runStartedAtRef.current,
      conditions,
      samples,
      stats: computeRunStats(samples.map((s) => s.duration)),
      // 終值快照取「本輪最後一批 metrics」。它們是累計值不是增量，所以最後一批就是全貌。
      customFinal: last ? { ...last.custom } : undefined,
      lcpFinal: last?.lcp ?? null,
      clsFinal: last?.cls ?? null,
    };
    setHistory((h) => [...h, finished]);
  }

  function startNewRun(): string {
    const id = nextRunId();
    runIdRef.current = id;
    runStartedAtRef.current = nowEpoch();
    runSamplesRef.current = new Map();
    lastSeqRef.current = null;
    // 換標本 / 切 mode 走的也是這條路，但它們不會送 host:reset，
    // 閘門要先關掉，否則會一直等一個永遠不來的 seq === 1。rerun() 隨後才把它打開。
    awaitingResetRef.current = false;
    bufRef.current = emptyBuffer(bufRef.current.notes);
    setRunId(id);
    commit();
    return id;
  }

  function rerun(): void {
    finalizeRun();
    const id = startNewRun();
    // 送出 host:reset 到 iframe 真的處理完之間，250ms 的 flush 可能已經先發了一批
    // **上一輪**的 metrics。切 mode 那條路徑有 `msg.mode !== modeRef.current` 擋著，
    // 重跑沒有 —— mode 沒變，那批舊樣本會直接被算進新一輪。
    // 標本端 reset 後 seq 從 1 重新開始，所以「等到 seq === 1 才收」就是乾淨的閘門。
    awaitingResetRef.current = true;
    post({ type: 'host:reset', v: PROTOCOL_VERSION, sessionId, runId: id });
  }

  function switchMode(next: string): void {
    if (next === modeRef.current) return;
    finalizeRun();
    modeRef.current = next;
    setMode(next);
    startNewRun();

    if (meta.switchKind === 'live') {
      // A 類：換掉 handler 實作就好，**不要 reload**。reload 會連 warmup 語意一起洗掉
      post({ type: 'host:set-mode', v: PROTOCOL_VERSION, sessionId, mode: next });
    } else {
      // B 類：LCP 載入後定案、CLS 累積整個 page lifetime，只能整份重載。
      // Phase 0 沒有 B 類標本，但 URL 契約已經凍結，這條分支必須先存在（spec §3.4）。
      setIframeSrc(specimenUrl(meta, next, validate));
    }
  }

  /** 切交叉驗證一定要重載：web-vitals 是在 iframe 開機時決定要不要動態 import 的 */
  function toggleValidate(next: boolean): void {
    finalizeRun();
    setValidate(next);
    startNewRun();
    // 用參數 next 而不是 state validate —— setState 是非同步的，這一行讀到的還是舊值
    setIframeSrc(specimenUrl(meta, modeRef.current, next));
  }

  function switchSpecimen(id: SpecimenId): void {
    const next = getSpecimen(id);
    if (!next || id === specimenIdRef.current) return;
    finalizeRun();
    const m = firstMode(next);
    specimenIdRef.current = id;
    modeRef.current = m;
    setSpecimenId(id);
    setMode(m);
    startNewRun();
    setIframeSrc(specimenUrl(next, m, validate));
  }

  const modes = [...meta.modes].sort((a, b) => a.order - b.order);
  const done = view.metrics?.totalInteractions ?? 0;

  return (
    <main className="shell">
      {import.meta.env.DEV && (
        // dev server 不 minify、不打包、有 HMR 開銷 —— 這裡量到的東西不是效能數字，
        // 是 Vite 的效能數字（陷阱 #3）。所以這條警告的功能是「作廢通知」，
        // 視覺上必須比頁面上任何東西都響，**不准因為變好看而變弱**。
        <p className="shell-void">
          <b className="shell-void__stamp">⛔ 檢驗無效 · DEV SERVER</b>
          這個頁面量到的數字全部無效。dev server 不 minify、不打包、還有 HMR 開銷，
          而且函式名沒有經過 build 的 keepNames 路徑。量測前必須跑{' '}
          <code className="shell-void__cmd">npm run measure</code>（build + preview）。
        </p>
      )}

      <header className="shell-mast">
        <p className="shell-mast__line">前端效能病理標本館</p>
        <h1 className="shell-mast__name">量測台</h1>
        <p className="shell-mast__meta">
          {/* build 與 protocol 是誠實原則的宣告：換 build 數字不可比。
              「npm run measure」那半句只對 clone repo 的人有意義，
              對站上訪客是天書 —— dev-only 的作廢橫幅（shell-void）才是它該住的地方。 */}
          Phase 3 · build <span className="shell-mono">{__BUILD_ID__}</span> · protocol v
          {PROTOCOL_VERSION} —— 數字受機器與負載影響，正式結論以{' '}
          <a className="shell-link" href="https://github.com/BardKidd/FrontendMuseum">
            原始資料（GitHub）
          </a>{' '}
          為準，可自行覆算。
        </p>
      </header>
      <hr className="shell-rule" />

      <nav className="shell-index" aria-label="標本索引">
        <h2 className="shell-h">標本</h2>
        <div className="shell-slots">
          {/*
            ⚠️ 按鈕的 textContent 是與量測工具的契約：`tools/reproducibility.mjs` 的
            `ptShell()` 與 `tools/acceptance.mjs` 都用 `textContent.includes(...)` 找它，
            比對字串逐字取自 `src/specimens.ts`。所以 `{s.id} {s.title}` 一個字都沒改，
            只包了兩個 span 讓 CSS 能分別排版 —— 中間那個 `{' '}` 是原本就有的空白字元，
            拿掉會讓 textContent 變成「01-main-thread-block主執行緒阻塞」。
          */}
          {SPECIMENS.map((s) => (
            <button
              key={s.id}
              className="shell-slot"
              onClick={() => switchSpecimen(s.id)}
              disabled={s.id === specimenId}
              /*
               * `disabled` 是「你正在看這個」的視覺表達（shell.css:266 的實墨框），
               * 但 disabled 的語意是「壞掉／不可用」，而且不可聚焦 ——
               * 鍵盤 Tab 會直接跳過目前這一顆，螢幕閱讀器只會唸到「按鈕，停用」。
               * aria-current 補的就是那句沒被唸出來的「目前選取」。
               *
               * ⚠️ 只加屬性，不動 disabled：`tools/reproducibility.mjs` 的 ptShell()
               * 與 acceptance.mjs 靠 textContent 找按鈕、靠 disabled 判斷是否已在該狀態，
               * 拿掉 disabled 會同時改變兩支工具的點擊路徑。
               */
              aria-current={s.id === specimenId ? 'true' : undefined}
            >
              <span className="shell-slot__no">{s.id}</span>{' '}
              <span className="shell-slot__name">{s.title}</span>
            </button>
          ))}
        </div>
      </nav>

      <section className="shell-label">
        <h2 className="shell-label__title">{meta.title}</h2>
        <p className="shell-label__sub">{meta.subtitle}</p>
        {/* 校準件不是標本，沒有病理報告（它的角色是證明量測層沒說謊） */}
        {!meta.id.startsWith('00') && (
          <p className="shell-label__report">
            <a className="shell-link shell-link--nowrap" href={`/reports/${meta.id}.html`}>
              病理報告：為什麼會發生、治療翻了哪個變因 →
            </a>
          </p>
        )}

        <h3 className="shell-h">
          切換（{meta.switchKind === 'live' ? '即時切換，不重載' : '重載整個 iframe'}）
        </h3>
        <ul className="shell-modes">
          {modes.map((m) => {
            const lesion = m.kind === 'pathological';
            return (
              <li key={m.id} className="shell-mode">
                {/*
                  ⚠️ `{m.label}` 是這顆按鈕唯一的子節點。`ptShell()` 用**全稱**比對
                  （標本 #6 有「治療二」與「治療二乙」，前綴比對會靜默誤擊），
                  多一個字就點不到。「病變 / 治療」放按鈕外面，而且不寫成
                  `病變：{m.label}` —— specimens.ts 的 label 本身已含前綴，
                  那樣會變成「病變：病變：全部渲染」。
                */}
                <button
                  className={`shell-mode__btn${lesion ? ' shell-mode__btn--lesion' : ''}`}
                  onClick={() => switchMode(m.id)}
                  disabled={m.id === mode}
                  /* 與標本按鈕同一個理由：disabled 表達「目前這一個」，
                     aria-current 才是把那件事說出口的屬性 */
                  aria-current={m.id === mode ? 'true' : undefined}
                >
                  {m.label}
                </button>
                <span className={`shell-mode__kind${lesion ? ' shell-mode__kind--lesion' : ''}`}>
                  {lesion ? '病變' : '治療'}
                </span>
                {m.requires !== undefined && m.requires.length > 0 && (
                  <span className="shell-mode__req">
                    {m.requires.map((r) => REQUIRES_NOTE[r]).join(' · ')}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      </section>

      <div className="shell-stage">
        <div className="shell-exhibit">
          {/*
            width / height 用 HTML 屬性寫死 800×600，取自 meta.viewport。
            絕對不要用百分比或 vh：CLS = impact fraction × distance fraction，兩者都是 viewport
            相對量，LCP element 的選擇也依賴 viewport —— 改尺寸等於讓所有歷史數字作廢（spec §4.6）。
            外框（.shell-frame）視窗變窄時橫向捲，**不縮 iframe**；左欄的 800px 也是硬值，
            CSS 那邊同樣不准出現 % / vw / transform: scale。
          */}
          {/*
            操作提示**必須在展示框正上方**。
            舊版擺在框底下：1280 寬時 iframe 裡要點的按鈕在 y≈528、節拍器在 y≈1067，
            相距 540px —— 而這個標本的整個前提是「照著拍子點十次」。
            拍子與按鈕不可能同時進視野，等於要求操作者記著節奏去點另一個地方。

            ⚠️ `.shell-pace` 鎖了 min-height（shell.css）：翻拍時字串長度會變
            （「第 9 / 10 拍」→「第 10 / 10 拍」），窄螢幕下換行數一變就會把 iframe 往下推，
            那是**待量的那一幀裡的版面工作**。鎖住高度之後，一行與兩行佔同一個盒子。
          */}
          <section className="shell-cue">
            <h3 className="shell-h">操作程序</h3>
            <p className="shell-instruction">{meta.protocol.instruction}</p>
            <p className="shell-pace">
              {/* 機器節拍的標本不渲染節拍器：它的 setInterval + 每拍一次 setState
                  會落在待量的那一段裡，而標本 #1 的兇手段正是 presentation。
                  人也照不了 17ms 的拍子 —— 照著做出來的是另一個實驗。 */}
              {meta.protocol.intervalMs !== null && !meta.protocol.machinePaced && (
                <Metronome
                  key={runId}
                  intervalMs={meta.protocol.intervalMs}
                  repetitions={meta.protocol.repetitions}
                />
              )}{' '}
              <span className="shell-count">
                {/* stream 的 done 數的是「按開始」那一下 —— repetitions 是 1，按下的
                    瞬間就顯示 1/1，而量測窗才剛開始。idle 的 done 恆為 0，印 0/1
                    會永遠像沒達標。兩者都不能沿用「已記錄 n / N」的達標語意。 */}
                {meta.protocol.action === 'idle'
                  ? '零互動標本 —— 不點不捲，樣本由標本自動記錄'
                  : meta.protocol.action === 'stream'
                    ? `已觸發 ${done} / ${meta.protocol.repetitions} 次 —— 量測窗這時才開始，由標本自行計時（外殼無從得知它何時結束）`
                    : `已記錄 ${done} / ${meta.protocol.repetitions} 次`}
              </span>
            </p>
          </section>

          <div className="shell-frame">
            <iframe
              key={meta.id}
              ref={iframeRef}
              src={iframeSrc}
              width={meta.viewport.width}
              height={meta.viewport.height}
              title={`${meta.id} 實驗區`}
            />
          </div>
          <p className="shell-caption">
            viewport{' '}
            <span className="shell-mono">
              {meta.viewport.width}×{meta.viewport.height}
            </span>{' '}
            · 尺寸凍結 —— CLS 與 LCP 都是 viewport 相對量，改它等於讓已登記的數字作廢。
            視窗比 800px 窄時這個框**橫向可捲**，不縮 iframe（縮了數字就作廢）
          </p>

          <section className="shell-block">
            <h3 className="shell-h">程序的凍結變因（spec §5.1 第 4 項）</h3>
            <p className="shell-note">
              {/* intervalMs null 不一定是「盡快連續」：#5（idle）是「靜置三秒不要碰」、
                  #6（stream）是「按一次然後放手」。這裡曾對兩者都印「盡快連續（不要等
                  畫面回應）」—— 與 instruction 相反的指示，人手複驗會照著做錯事。
                  「盡快連續」只對 click／scroll／type 的 null 成立（protocol.ts 的語意）。 */}
              動作 {meta.protocol.action} · 次數 {meta.protocol.repetitions} · 間隔{' '}
              {meta.protocol.intervalMs !== null
                ? `${meta.protocol.intervalMs}ms`
                : meta.protocol.action === 'idle'
                  ? '—（零互動，沒有間隔可言）'
                  : meta.protocol.action === 'stream'
                    ? '—（單次觸發，之後由標本自行計時）'
                    : '盡快連續（不要等畫面回應）'}
            </p>
          </section>

          <section className="shell-block">
            <h3 className="shell-h">凍結條件</h3>
            <div className="shell-field">
              <label className="shell-field__label">
                CPU throttle（JS 偵測不到 DevTools 的設定，只能自己宣告）{' '}
                {/* ⚠️ 整頁只准有這一個 <select>：tools/acceptance.mjs 與量測工具都用
                    document.querySelector('select') 抓它來設 throttle 宣告值。 */}
                <select
                  className="shell-select"
                  value={cpuThrottle}
                  onChange={(e) => setCpuThrottle(e.target.value as CpuThrottle)}
                >
                  {THROTTLE_OPTIONS.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </label>
              <button className="shell-btn-primary" onClick={rerun}>
                重跑（把這一輪存進歷史，開新的一輪）
              </button>
            </div>
            {/*
              未宣告的警示過去只長在右欄的面板裡（Panel.tsx 的 Conditions）——
              控制項在左欄底部、警示在右欄中段，看到警示的人要跨欄去找該按哪裡。
              面板那一份留著（它是快照的一部分，截圖要看得到），這一份負責就地可修。
            */}
            {cpuThrottle === 'unknown' && (
              <p className="shell-alert">
                ⚠ 還沒宣告 —— JS 偵測不到 DevTools 的節流倍率，這一欄空著的話，
                現在截圖之後沒有人知道它是幾倍速，等同作廢。DevTools → Performance →
                齒輪 → CPU 選好之後，回上面那個下拉把它宣告一次。
              </p>
            )}
            <label className="shell-check">
              <input
                type="checkbox"
                checked={validate}
                onChange={(e) => toggleValidate(e.target.checked)}
              />{' '}
              web-vitals 交叉驗證（會重載 iframe）
            </label>
            <p className="shell-note">
              —— 預設關閉：web-vitals 自己那組 observer 會污染 baseline，只在對帳時開（陷阱 #7）。
              對帳結果同時進面板的 crossCheck 欄與 iframe 的 console.table。
            </p>
          </section>
        </div>

        <Panel
          meta={meta}
          mode={mode}
          runId={runId}
          conditions={conditions}
          ready={view.ready}
          metrics={view.metrics}
          loaf={view.loaf}
          loafSupported={LOAF_SUPPORTED}
          history={history}
          notes={view.notes}
        />
      </div>
    </main>
  );
}
